/**
 * Automation UI menu ids + dropdown options (same role as Job/config.jsx, Kanban/config.jsx).
 * Presentation-only until wired to Alpaca / backend.
 */
export const AUTOMATION_MENU_OPTION_IDS = {
    MESSAGE_APPLICANT: 'message_applicant',
    MESSAGE_COLLABORATORS: 'message_collaborators',
    MOVE_STEP: 'move_step',
    ADD_TAG: 'add_tag',
    ADD_NOTE: 'add_note',
};

export const AUTOMATION_MENU_OPTIONS = [
    {
        id: AUTOMATION_MENU_OPTION_IDS.MESSAGE_APPLICANT,
        label: 'Message applicant',
        iconClass: 'icon-svg-message-heart-square',
    },
    {
        id: AUTOMATION_MENU_OPTION_IDS.MESSAGE_COLLABORATORS,
        label: 'Message collaborators',
        iconClass: 'icon-svg-message-notification-square',
    },
    {
        id: AUTOMATION_MENU_OPTION_IDS.MOVE_STEP,
        label: 'Move step',
        iconClass: 'icon-svg-flow-horizontal',
    },
    {
        id: AUTOMATION_MENU_OPTION_IDS.ADD_TAG,
        label: 'Add a tag',
        iconClass: 'icon-svg-tag-03',
    },
    {
        id: AUTOMATION_MENU_OPTION_IDS.ADD_NOTE,
        label: 'Add a note',
        iconClass: 'icon-svg-edit-05',
    },
];

export const AUTOMATION_TRIGGER_IDS = {
    SCHEDULED: 'scheduled',
};

export function isScheduledTriggerId(triggerId) {
    return triggerId === AUTOMATION_TRIGGER_IDS.SCHEDULED;
}

/** Placeholder triggers for the add-automation modal sidebar (UI only). */
export const AUTOMATION_TRIGGER_OPTIONS = [
    { id: AUTOMATION_TRIGGER_IDS.SCHEDULED, label: 'Scheduled trigger' },
    { id: 'on_outcome', label: 'When outcome is recorded' },
    { id: 'on_enter_step', label: 'When entering step' },
    { id: 'on_exit_step', label: 'When leaving step' },
];

/** Scheduled trigger: time of day (UI only). */
export const SCHEDULE_TIME_OPTIONS = [
    { id: '08:00', label: '8:00' },
    { id: '09:00', label: '9:00' },
    { id: '10:00', label: '10:00' },
    { id: '12:00', label: '12:00' },
    { id: '15:00', label: '15:00' },
    { id: '17:00', label: '17:00' },
];

/**
 * Application questions for scheduled-condition builder (UI only).
 * `choices` use stable ids for checkbox state.
 */
export const SCHEDULE_CONDITION_QUESTIONS = [
    {
        id: 'licence',
        label: 'What licence do you have?',
        choices: [
            { id: 'a', label: 'Remote' },
            { id: 'b', label: 'B' },
            { id: 'c', label: 'C' },
            { id: 'd', label: 'D' },
            { id: 'e', label: 'E' },
            { id: 'f', label: 'F' },
        ],
    },
    {
        id: 'experience',
        label: 'Years of experience?',
        choices: [
            { id: 'y0_2', label: '0–2 years' },
            { id: 'y3_5', label: '3–5 years' },
            { id: 'y5p', label: '5+ years' },
        ],
    },
    {
        id: 'location',
        label: 'Preferred work location?',
        choices: [
            { id: 'remote', label: 'Remote' },
            { id: 'office', label: 'Office' },
            { id: 'hybrid', label: 'Hybrid' },
        ],
    },
];

/** Optional conditions menu (matches product design; not persisted yet). */
export const AUTOMATION_CONDITION_OPTIONS = [
    { id: 'app_specific_answer', label: 'Application has a specific answer' },
    { id: 'eval_positive', label: 'Evaluation - positive' },
    { id: 'eval_negative', label: 'Evaluation - negative' },
    { id: 'eval_overall', label: 'Evaluation overall impression' },
    { id: 'video_answers', label: 'Video answers' },
    { id: 'video_app_answers', label: 'Video application answers' },
    { id: 'status', label: 'Status' },
    { id: 'outcome', label: 'Outcome' },
    { id: 'recruitment_report', label: 'State of recruitment report' },
    { id: 'form_filled', label: 'Form filled' },
    { id: 'my_role', label: 'My role in job' },
    { id: 'talent_community', label: 'Talent Community' },
];

export const MOVE_STEP_OPTIONS = [
    { id: 'applied', name: 'Applied' },
    { id: 'screening', name: 'Screening' },
    { id: 'phone_interview', name: 'Phone interview' },
    { id: 'interview', name: 'Interview' },
    { id: 'assessment', name: 'Assessment' },
    { id: 'offer', name: 'Offer' },
    { id: 'hired', name: 'Hired' },
    { id: 'rejected', name: 'Rejected' },
    { id: 'custom', name: 'Custom' },
];

export const AUTOMATION_SEND_TIMING_OPTIONS = [
    { id: 'immediate', label: 'Send email immediately' },
    { id: 'delayed_1h', label: 'Send after 1 hour' },
    { id: 'delayed_24h', label: 'Send after 24 hours' },
];

/** Message body templates (UI only; wire to CMS later). */
export const AUTOMATION_MESSAGE_TEMPLATE_OPTIONS = [
    { id: 'blank', label: 'Blank message' },
    { id: 'welcome', label: 'Welcome' },
    { id: 'follow_up', label: 'Follow-up' },
    { id: 'interview', label: 'Interview invitation' },
];

export const AUTOMATION_LANGUAGE_OPTIONS = [
    { code: 'en', name: 'English' },
    { code: 'fi', name: 'Finnish' },
    { code: 'sv', name: 'Swedish' },
];

/** Modal title per automation type */
export function automationModalTitle(option, t) {
    if (!option) return '';
    switch (option.id) {
        case AUTOMATION_MENU_OPTION_IDS.MESSAGE_APPLICANT:
            return t('Send automated message to applicants');
        case AUTOMATION_MENU_OPTION_IDS.MESSAGE_COLLABORATORS:
            return t('Send automated message to collaborators');
        case AUTOMATION_MENU_OPTION_IDS.MOVE_STEP:
            return t('Move application to another step');
        case AUTOMATION_MENU_OPTION_IDS.ADD_TAG:
            return t('Add a tag to the application');
        case AUTOMATION_MENU_OPTION_IDS.ADD_NOTE:
            return t('Add an internal note');
        default:
            return option.label;
    }
}

export function isMessageAutomationKind(kind) {
    return (
        kind === AUTOMATION_MENU_OPTION_IDS.MESSAGE_APPLICANT ||
        kind === AUTOMATION_MENU_OPTION_IDS.MESSAGE_COLLABORATORS
    );
}

/**
 * Returns the chip icon class associated with a given automation kind, sourced
 * from AUTOMATION_MENU_OPTIONS so the chip and the "Add automation" dropdown
 * stay in lock-step (e.g. picking "Message applicant" shows the same icon on
 * the resulting chip).
 */
export function iconForAutomationKind(kind) {
    const opt = AUTOMATION_MENU_OPTIONS.find((o) => o.id === kind);
    return opt ? opt.iconClass : AUTOMATION_MENU_OPTIONS[0].iconClass;
}
